import { useCallback, useMemo } from 'react';
import {
  buildEditorsHelperText,
  isPersonInSelection,
  mapEditorToAutocompleteOption,
} from '../../../../utils/case-plan-create.util';
import { useEditorSearchHook } from './useEditorSearchHook';

/**
 * Identity match between an existing editor and a remapped option.
 * Both sides must carry the same identifier for a match: manage-editors chips have no
 * `username` and search results can have an empty one, so a bare `a === b` would treat
 * every such pair as the same person and copy the first chip's flags onto all of them.
 * @param {object} editor
 * @param {object} candidate
 * @returns {boolean}
 */
const isSameEditor = (editor, candidate) =>
  (editor.personId != null && editor.personId === candidate.personId) ||
  (Boolean(editor.username) && editor.username === candidate.username);

/**
 * State, search, and selection logic for the Editors section.
 * Keeps EditorsSection purely presentational.
 * @param {object[]} editors
 * @param {(editors: object[]) => void} onEditorsChange
 * @param {{ includeCreatorDefault?: boolean }} [options]
 */
export const useEditorsSectionHook = (
  editors,
  onEditorsChange,
  { includeCreatorDefault = true } = {},
) => {
  const { options, isLoading, handleSearchChange } = useEditorSearchHook();

  const selectedMembers = useMemo(
    () => editors.map(mapEditorToAutocompleteOption).filter(Boolean),
    [editors],
  );

  const memberOptions = useMemo(
    () => options.map(mapEditorToAutocompleteOption).filter(Boolean),
    [options],
  );

  const handleMembersChange = useCallback(
    (nextValue) => {
      const nextEditors = Array.isArray(nextValue) ? nextValue : [];
      // Preserve the creator flag when options are remapped after a search.
      const withPreservedFlags = nextEditors.map((editor) => {
        const existing = editors.find((item) => isSameEditor(item, editor));
        return {
          ...editor,
          isCreator: Boolean(existing?.isCreator || editor.isCreator),
          isSelf: Boolean(existing?.isSelf || editor.isSelf),
          hasEditPermission: existing?.hasEditPermission ?? editor.hasEditPermission,
        };
      });
      onEditorsChange(withPreservedFlags);
    },
    [editors, onEditorsChange],
  );

  const handleRemoveEditor = useCallback(
    (editorToRemove) => {
      if (editorToRemove.isCreator || editorToRemove.isSelf) {
        return;
      }
      onEditorsChange(
        editors.filter((editor) => editor.personId !== editorToRemove.personId),
      );
    },
    [editors, onEditorsChange],
  );

  const getOptionDisabled = useCallback(
    (option) => isPersonInSelection(option, editors),
    [editors],
  );

  const helperText = buildEditorsHelperText(editors.length, { includeCreatorDefault });

  return {
    isLoading,
    selectedMembers,
    memberOptions,
    helperText,
    handleSearchChange,
    handleMembersChange,
    handleRemoveEditor,
    getOptionDisabled,
  };
};
